import { useMutation, useQuery } from '@apollo/client/react'
import {
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { AdminPageLayout } from '../../../components/AdminPageLayout/AdminPageLayout'
import { AdminBadge } from '../../../components/StatusBadge/StatusBadge'
import { showToast } from '../../../components/Toast/showToast'
import type {
  CustomerDataApiDeployment,
  DeploymentStatus,
} from '../../../services/customerDataApiService'
import {
  cancelCustomerDataApiDeploymentMutation,
  customerDataApiConfigByIdQuery,
  customerDataApiDeploymentByIdQuery,
  rerunCustomerDataApiDeploymentMutation,
} from '../../../services/customerDataApiService'
import { formatAdminDateTime } from '../../../utils/dates'
import {
  LogStepRow,
  MetaCell,
  getDeploymentOutcomeLabel,
  getOutcomeSummary,
  isTerminalStatus,
  parseLogIntoSteps,
  statusColor,
} from './DeploymentDetailsAdminPage.parts'

export const DeploymentDetailsAdminPage = () => {
  const { id } = useParams<{ id: string }>()
  const [lastActiveAt, setLastActiveAt] = useState<number>(Date.now())
  const [detailPollMs, setDetailPollMs] = useState(0)

  const {
    data: deploymentData,
    loading: isLoading,
    error,
  } = useQuery(customerDataApiDeploymentByIdQuery, {
    variables: { id: id ?? '' },
    skip: !id,
    fetchPolicy: 'network-only',
    pollInterval: detailPollMs,
  })

  const deployment = ((
    deploymentData as { customerDataApiDeploymentById?: unknown } | undefined
  )?.customerDataApiDeploymentById ?? null) as CustomerDataApiDeployment | null

  useEffect(() => {
    if (deployment?.lastProgressAt) {
      setLastActiveAt(new Date(deployment.lastProgressAt as string).getTime())
    }
  }, [deployment?.lastProgressAt])

  useEffect(() => {
    if (!deployment) {
      setDetailPollMs(0)
      return
    }
    const status = deployment.status as DeploymentStatus
    if (isTerminalStatus(status)) {
      setDetailPollMs(0)
      return
    }
    const idleMs = Date.now() - lastActiveAt
    setDetailPollMs(idleMs > 10 * 60 * 1000 ? 10000 : 2000)
  }, [deployment, lastActiveAt])

  const { data: configData } = useQuery(customerDataApiConfigByIdQuery, {
    variables: { id: deployment?.configId ?? '' },
    skip: !deployment?.configId,
    fetchPolicy: 'network-only',
  })
  const config =
    (configData as { customerDataApiConfigById?: unknown } | undefined)
      ?.customerDataApiConfigById ?? null

  const deploymentDetailRefetch = {
    query: customerDataApiDeploymentByIdQuery,
    variables: { id: id ?? '' },
  }

  const [cancelMutation, { loading: cancelMutationLoading }] = useMutation(
    cancelCustomerDataApiDeploymentMutation,
    {
      refetchQueries: id ? [deploymentDetailRefetch] : [],
      awaitRefetchQueries: true,
      onCompleted: () => {
        showToast({
          title: 'Cancellation requested',
          variant: 'success',
        })
      },
      onError: (err: Error) =>
        showToast({
          title: 'Cancel failed',
          description: err.message,
          variant: 'error',
        }),
    },
  )

  const [rerunMutation, { loading: rerunMutationLoading }] = useMutation(
    rerunCustomerDataApiDeploymentMutation,
    {
      refetchQueries: ['CustomerDataApiDeployments'],
      awaitRefetchQueries: true,
      onCompleted: result => {
        const newDep = (
          result as {
            rerunCustomerDataApiDeployment?: CustomerDataApiDeployment
          }
        )?.rerunCustomerDataApiDeployment
        showToast({
          title: 'Re-run started',
          description: `New pipeline run ${newDep?.id?.slice(0, 8) ?? ''}… created.`,
          variant: 'success',
        })
      },
      onError: (err: Error) =>
        showToast({
          title: 'Re-run failed',
          description: err.message,
          variant: 'error',
        }),
    },
  )

  const configDetails = config as {
    deployments?: unknown[]
  } | null

  const artifactSummaries = useMemo(() => {
    const smrtVersions =
      (
        deployment as {
          smrtVersions?: {
            subgraphName: string
            smrtVersion: string
            smrtIri: string
          }[]
        }
      )?.smrtVersions ?? []
    return smrtVersions.map(entry => ({
      subgraphName: entry.subgraphName,
      smrtVersion: entry.smrtVersion,
      smrtIri: entry.smrtIri,
    }))
  }, [deployment])

  const relatedRuns = useMemo(
    () => (configDetails?.deployments ?? []) as any[],
    [configDetails],
  )

  const logSteps = useMemo(
    () => parseLogIntoSteps(deployment?.logs ?? ''),
    [deployment?.logs],
  )

  if (!id) {
    return (
      <AdminPageLayout pageTitle="Pipeline run">
        <Text color="presets.neutral.70">Missing deployment ID.</Text>
        <Button as={Link} to="/admin/deployments" variant="outline" mt={4}>
          Back to Deployments
        </Button>
      </AdminPageLayout>
    )
  }

  if (isLoading && !deployment) {
    return (
      <AdminPageLayout pageTitle="Pipeline run">
        <Flex justify="center" py={12}>
          <Spinner />
        </Flex>
      </AdminPageLayout>
    )
  }

  if (error || !deployment) {
    return (
      <AdminPageLayout pageTitle="Pipeline run">
        <Text color="presets.pink.70">
          {error ? (error as Error).message : 'Deployment not found.'}
        </Text>
        <Button as={Link} to="/admin/deployments" variant="outline" mt={4}>
          Back to Deployments
        </Button>
      </AdminPageLayout>
    )
  }

  const status = deployment.status as DeploymentStatus
  const isInFlight = !isTerminalStatus(status)

  return (
    <AdminPageLayout pageTitle="Pipeline run">
      <VStack align="stretch" gap={6} pb={8}>
        <Flex align="center" justify="space-between" flexWrap="wrap" gap={3}>
          <Button
            as={Link}
            to="/admin/deployments"
            variant="ghost"
            size="sm"
            data-testid="back-to-deployments"
          >
            ← Back to Deployments
          </Button>
          <Flex gap="2">
            {isInFlight ? (
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                isLoading={cancelMutationLoading}
                onClick={() =>
                  void cancelMutation({ variables: { deploymentId: id } })
                }
                data-testid="cancel-deployment-button"
              >
                Cancel pipeline
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                isLoading={rerunMutationLoading}
                onClick={() =>
                  void rerunMutation({ variables: { deploymentId: id } })
                }
                data-testid="rerun-deployment-button"
              >
                Re-run pipeline
              </Button>
            )}
          </Flex>
        </Flex>

        <Box
          borderWidth="1px"
          borderRadius="lg"
          p={4}
          bg="presets.neutral.5"
          borderColor="presets.neutral.20"
        >
          <Flex align="center" gap={2} mb={3} flexWrap="wrap">
            <Heading size="sm">
              Pipeline run #{id.slice(0, 8)}… · attempt{' '}
              {deployment.attempt ?? 1}
            </Heading>
            <AdminBadge colorScheme={statusColor[status]}>
              {getDeploymentOutcomeLabel(deployment)}
            </AdminBadge>
            {deployment.isActive && (
              <AdminBadge colorScheme="green">LIVE</AdminBadge>
            )}
          </Flex>
          <Text fontSize="sm" color="presets.neutral.70" mb={3}>
            {getOutcomeSummary(deployment)}
          </Text>
          <Flex gap={8} flexWrap="wrap" align="center">
            <MetaCell label="Started">
              {formatAdminDateTime(deployment.startedAt) ?? '—'}
            </MetaCell>
            {deployment.completedAt && (
              <MetaCell label="Completed">
                {formatAdminDateTime(deployment.completedAt) ?? '—'}
              </MetaCell>
            )}
            {deployment.canceledAt && (
              <MetaCell label="Canceled">
                {formatAdminDateTime(deployment.canceledAt) ?? '—'}
                {deployment.canceledByUser
                  ? ` by ${deployment.canceledByUser.name ?? deployment.canceledByUser.email}`
                  : ''}
              </MetaCell>
            )}
            <MetaCell label="Triggered by">
              {deployment.deployedByUser?.name ??
                deployment.deployedByUser?.email ??
                deployment.deployedByUserId}
            </MetaCell>
            <MetaCell label="Config">
              <Link
                to={`/admin/customer-data-api-config?configId=${deployment.configId}`}
                style={{
                  color: 'var(--chakra-colors-presets-turquoise-70)',
                  fontSize: '0.875rem',
                }}
              >
                {(() => {
                  const d = deployment as {
                    config?: { createdDate?: string }
                  }
                  return d.config?.createdDate
                    ? formatAdminDateTime(d.config.createdDate)
                    : deployment.configId
                })()}
              </Link>
            </MetaCell>
          </Flex>
        </Box>

        {artifactSummaries.length > 0 && (
          <Box>
            <Heading size="sm" mb={2}>
              SMRT artifacts used
            </Heading>
            <VStack align="stretch" gap={2}>
              {artifactSummaries.map(a => (
                <Flex
                  key={a.subgraphName}
                  align="center"
                  gap={4}
                  p={2}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor="presets.neutral.20"
                  bg="presets.neutral.5"
                >
                  <AdminBadge colorScheme="purple">{a.subgraphName}</AdminBadge>
                  <Text fontSize="sm">
                    Version: <b>{a.smrtVersion ?? '(unknown)'}</b>
                  </Text>
                  {a.smrtIri && (
                    <Text
                      fontSize="xs"
                      color="presets.neutral.70"
                      fontFamily="mono"
                      noOfLines={1}
                      maxW="400px"
                    >
                      {a.smrtIri}
                    </Text>
                  )}
                  {a.smrtVersion && (
                    <Link
                      to={`/admin/smrt-artifacts?version=${a.smrtVersion}`}
                      style={{
                        marginLeft: 'auto',
                        color: 'var(--chakra-colors-presets-turquoise-70)',
                        fontSize: '12px',
                      }}
                    >
                      open artifact
                    </Link>
                  )}
                </Flex>
              ))}
            </VStack>
          </Box>
        )}

        <Box>
          <Heading size="sm" mb={2}>
            Logs
          </Heading>
          {logSteps.length === 0 ? (
            <Box
              as="pre"
              fontFamily="mono"
              fontSize="xs"
              whiteSpace="pre-wrap"
              wordBreak="break-word"
              p={4}
              bg="presets.neutral.10"
              borderRadius="md"
              borderWidth="1px"
              borderColor="presets.neutral.20"
            >
              {deployment.logs || '(no logs yet)'}
            </Box>
          ) : (
            <VStack align="stretch" gap={1}>
              {logSteps.map((step, i) => (
                <LogStepRow key={i} step={step} />
              ))}
            </VStack>
          )}
        </Box>

        {relatedRuns.length > 1 && (
          <Box>
            <Heading size="sm" mb={2}>
              Related runs for this config
            </Heading>
            <VStack align="stretch" gap={1}>
              {relatedRuns
                .filter((d: any) => d.id !== id)
                .slice(0, 10)
                .map((d: any) => (
                  <Flex
                    key={d.id}
                    align="center"
                    justify="space-between"
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor="presets.neutral.20"
                    p={2}
                  >
                    <Flex gap={3} align="center">
                      <AdminBadge
                        colorScheme={statusColor[d.status as DeploymentStatus]}
                      >
                        {d.status}
                      </AdminBadge>
                      <Text fontSize="xs" color="presets.neutral.70">
                        attempt {d.attempt ?? 1} ·{' '}
                        {formatAdminDateTime(d.startedAt) ?? '—'}
                      </Text>
                    </Flex>
                    <Button
                      as={Link}
                      to={`/admin/deployments/${d.id}`}
                      size="xs"
                      variant="ghost"
                    >
                      view
                    </Button>
                  </Flex>
                ))}
            </VStack>
          </Box>
        )}
      </VStack>
    </AdminPageLayout>
  )
}
